import { get, post } from '@/lib/api';
import type {
    DappChain,
    DappDeployTarget,
    DappFramework,
    DappGenerateRequest,
    DappGenerateResponse,
    DappGeneratedFile,
    DappProject,
} from '@/types/dapp-builder';

/* ================================================================== */
/*  dApp Builder Service                                              */
/*                                                                    */
/*  Backend API calls for dApp scaffolding, AI generation,            */
/*  and deployment.                                                   */
/* ================================================================== */

/** Generate a full dApp from an AI prompt */
export async function generateDapp(req: DappGenerateRequest): Promise<DappGenerateResponse> {
  return post<DappGenerateResponse>('/ide/dapp/generate', req);
}

/** Scaffold a dApp from a template (no AI) */
export async function scaffoldFromTemplate(
  templateId: string,
  name: string,
  framework: DappFramework,
  chains: DappChain[],
): Promise<DappGenerateResponse> {
  return post<DappGenerateResponse>('/ide/dapp/scaffold', {
    templateId,
    name,
    framework,
    chains,
  });
}

/** Get generated file list for a dApp project */
export async function getDappFiles(projectId: string): Promise<DappGeneratedFile[]> {
  return get<DappGeneratedFile[]>(`/ide/dapp/${projectId}/files`);
}

/** Deploy dApp frontend + contracts */
export async function deployDapp(
  projectId: string,
  target: DappDeployTarget,
): Promise<{ url: string; contractAddresses: Record<string, string> }> {
  return post<{ url: string; contractAddresses: Record<string, string> }>(
    `/ide/dapp/${projectId}/deploy`,
    target,
  );
}

/** Get dApp project details */
export async function getDappProject(projectId: string): Promise<DappProject> {
  return get<DappProject>(`/ide/dapp/${projectId}`);
}

/** List user's dApp projects */
export async function listDappProjects(): Promise<DappProject[]> {
  const res = await get<{ projects: DappProject[] } | DappProject[]>('/ide/dapp/projects');
  return Array.isArray(res) ? res : (res.projects ?? []);
}
